import { appQueryClient } from "app/AppQueryClient";
import { useIsLoggedIn } from "features/auth/AuthUtils";
import {
  getUsersMeRetrieveQueryKey,
  usersMeRetrieve,
  useUsersMeRetrieve,
} from "gen/endpoints/users/users";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { UserRoleTypeEnum } from "gen/model/userRoleTypeEnum";

// We want to cache feature flags but have caching turned off by default.
// We set them specifically for this request.
// https://tanstack.com/query/v4/docs/react/guides/caching
const STALE_TIME = 1 * 60 * 1000; // 1 minute
const CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export const useHasPermission = (permission: PermissionTypeEnum): boolean => {
  const isLoggedIn = useIsLoggedIn();
  const { data } = useUsersMeRetrieve({
    query: {
      enabled: isLoggedIn,
      staleTime: STALE_TIME,
      gcTime: CACHE_TIME,
    },
  });
  return data?.permission_types.includes(permission) || false;
};

export const hasPermission = async (
  permission: PermissionTypeEnum,
): Promise<boolean> => {
  const data = await appQueryClient.ensureQueryData({
    queryKey: getUsersMeRetrieveQueryKey(),
    queryFn: () => usersMeRetrieve(),
  });
  return data.permission_types.includes(permission) || false;
};

export const useUserRoles = (): UserRoleTypeEnum[] => {
  const isLoggedIn = useIsLoggedIn();
  const { data } = useUsersMeRetrieve({
    query: {
      enabled: isLoggedIn,
      staleTime: STALE_TIME,
      gcTime: CACHE_TIME,
      throwOnError: false,
    },
  });
  return data?.role_types || [];
};
